// Event names and property shapes for query permit telemetry.

// event names
export const QUERY_PERMIT_SOFT_THROTTLE_EVENT = `query_permit.soft_throttle`;
export const QUERY_PERMIT_POOL_STATS_EVENT = `query_permit.pool_stats`;

// properties
export enum QueryPermitClientType {
  SharedWorker = `SHARED_WORKER`,
  Local = `LOCAL`,
}

// Fields common to every query-permit event: the project the metric is scoped to and the client
// that emitted it.
export interface QueryPermitEventBase {
  project_id: number;
  client_type: QueryPermitClientType;
}

export interface SoftThrottleEventProps extends QueryPermitEventBase {
  // Number of queries that were throttled
  engaged_count: number;
  // Histogram of engaged grants by backoff depth level
  backoff_level_1: number;
  backoff_level_2: number;
  backoff_level_3: number;
  backoff_level_4: number;
  backoff_level_5_plus: number;
  // Query rate over the flush window
  sustained_rpm: number;
}

// One aggregate per project per flush window covering the semaphore's load behavior. Counts and sums
// are raw so windows re-aggregate correctly.
export interface PoolStatsEventProps extends QueryPermitEventBase {
  // Permits granted in the window that went through the queue and held a pool slot. Excludes
  // queue_full_grants.
  grant_count: number;
  // Of grant_count, those on the background (prefetch) pool tier.
  background_pool_grant_count: number;
  // Of grant_count, those from a background (non-viewed) tab. Worker-only; the local client is tab
  // agnostic, so it omits this.
  background_tab_grant_count?: number;

  // Queue / saturation
  sample_count: number;
  pending_peak: number;
  // Tabs with at least one outstanding acquire in this pool, queued or holding a slot. Connected
  // but idle tabs are excluded.
  active_port_peak: number;
  // Whether any of those active tabs was in the background (non-viewed) tier at sample time.
  background_active_port_samples?: number;
  // Samples where in-flight permits reached the cap.
  saturated_samples: number;
  // Acquires the worker granted without queuing because the pending queue was already full, so they
  // hold no pool slot.
  queue_full_grants: number;

  // Wait for a concurrency slot (enqueue->grant), in ms. Excludes soft-throttle backoff, which is
  // applied after the grant.
  wait_for_grant_ms_max: number;
  wait_for_grant_ms_total: number;

  // Involuntary reclaims of slot-holding permits (max-hold + port-dropped). Worker-only; the local
  // client cannot reclaim, so it omits this.
  reclaimed_total?: number;

  // Busy periods (queue non-empty -> empty) that completed in the window, and their durations in ms.
  drain_count: number;
  drain_ms_max: number;
  drain_ms_total: number;
}

export type SoftThrottleTrackEvent = {
  name: typeof QUERY_PERMIT_SOFT_THROTTLE_EVENT;
  properties: SoftThrottleEventProps;
};
export type PoolStatsTrackEvent = {
  name: typeof QUERY_PERMIT_POOL_STATS_EVENT;
  properties: PoolStatsEventProps;
};

export type QueryPermitTrackEvent = SoftThrottleTrackEvent | PoolStatsTrackEvent;
